import { useMemo, useState } from "react";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Code2, Copy, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable embed-code card.
 *
 * Props:
 *  - variant: "coach" | "institute"
 *  - registerUrl: absolute URL to the public /register page on this deployment
 *      (e.g. https://app.neurosofie.nl/register?institute=...&coach=...)
 *
 * Renders three flavours of embed snippet the coach/institute admin can
 * paste into their own website:
 *   1. iFrame — drops the full sign-up flow on their page.
 *   2. Button — a styled <a> tag that opens NeuroSophia in a new tab.
 *   3. Direct link — the raw URL for any bespoke embed (newsletters etc).
 */
export default function EmbedCodeCard({ variant, registerUrl }) {
  const { t } = useT();
  const [tab, setTab] = useState("iframe");

  const iframeSnippet = useMemo(
    () =>
      `<iframe
  src="${registerUrl}"
  title="NeuroSophia"
  style="width:100%; min-height:780px; border:0; border-radius:16px;"
  loading="lazy"
  allow="clipboard-write"
></iframe>`,
    [registerUrl]
  );

  const buttonSnippet = useMemo(
    () =>
      `<a
  href="${registerUrl}"
  target="_blank"
  rel="noopener"
  style="display:inline-block; padding:14px 28px; background:#4A5D4E; color:#ffffff; text-decoration:none; border-radius:10px; font-family:system-ui, -apple-system, sans-serif; font-weight:600; letter-spacing:0.01em;"
>${t("embed.button_label")}</a>`,
    [registerUrl, t]
  );

  const snippet = tab === "iframe" ? iframeSnippet : tab === "button" ? buttonSnippet : registerUrl;
  const [justCopied, setJustCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setJustCopied(true);
      setTimeout(() => setJustCopied(false), 2000);
      toast.success(t("embed.copy.toast"));
    } catch (e) {
      toast.error(t("embed.copy.error"));
    }
  };

  const tabBtn = (key, label) => (
    <button
      key={key}
      type="button"
      onClick={() => setTab(key)}
      data-testid={`embed-tab-${key}`}
      className={`px-3 py-2 text-xs font-bold uppercase tracking-[0.15em] rounded-lg border transition-colors ${
        tab === key
          ? "bg-[#4A5D4E] text-white border-[#4A5D4E]"
          : "bg-white text-[#5C635A] border-[#E5E2DC] hover:bg-[#F2F0EA]"
      }`}
    >
      {label}
    </button>
  );

  const hintKey =
    tab === "iframe" ? "embed.iframe.hint" : tab === "button" ? "embed.button.hint" : "embed.link.hint";

  return (
    <div
      className="bg-white border border-[#E5E2DC] rounded-2xl p-6 sm:p-8"
      data-testid={`embed-card-${variant}`}
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="h-10 w-10 rounded-xl bg-[#F2F0EA] flex items-center justify-center">
          <Code2 strokeWidth={1.5} className="text-[#B37A5F]" size={20} />
        </div>
        <div>
          <h3 className="font-heading text-xl text-[#1A1D1A] tracking-tight font-semibold">
            {t(`embed.${variant}.title`)}
          </h3>
          <p className="text-sm text-[#5C635A] mt-0.5 max-w-2xl leading-relaxed">
            {t(`embed.${variant}.subtitle`)}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-3" role="tablist" data-testid="embed-tabs">
        {tabBtn("iframe", t("embed.tab.iframe"))}
        {tabBtn("button", t("embed.tab.button"))}
        {tabBtn("link", t("embed.tab.link"))}
      </div>
      <p className="text-[11px] text-[#8A9188] leading-relaxed mb-3">{t(hintKey)}</p>

      <div className="relative">
        <textarea
          readOnly
          value={snippet}
          rows={tab === "link" ? 2 : 7}
          data-testid="embed-snippet"
          onFocus={(e) => e.target.select()}
          className="w-full rounded-lg bg-[#F9F8F6] border border-[#E5E2DC] px-3 py-2.5 text-xs font-mono leading-relaxed text-[#1A1D1A] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#4A5D4E] focus-visible:border-[#4A5D4E]"
        />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button
          type="button"
          onClick={copy}
          data-testid="embed-copy-button"
          className="h-11 rounded-lg bg-[#4A5D4E] hover:bg-[#3D4D41] text-white"
        >
          {justCopied ? (
            <>
              <Check strokeWidth={1.5} size={16} className="mr-2" />
              {t("embed.copied")}
            </>
          ) : (
            <>
              <Copy strokeWidth={1.5} size={16} className="mr-2" />
              {t("embed.copy")}
            </>
          )}
        </Button>
        <a
          href={registerUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="embed-preview-link"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-[#5C635A] hover:text-[#1A1D1A] font-bold"
        >
          <ExternalLink strokeWidth={1.5} size={14} />
          {t("embed.preview")}
        </a>
      </div>
    </div>
  );
}
